/**
 * Discounts v2 — the OMS v3 discount system.
 *
 * Discounts are individual "elements" created together in a "group" (an
 * exception price list, a bulk-discount batch, or an ingested Special Price
 * Request). Elements are stacked onto a specific draft sales-order line in a
 * deterministic application order, and the applied stack is frozen onto the
 * real sales-order line at submission. See `.claude/skills/discounts`.
 */

/** How a discount element changes the running unit price. */
export enum DiscountElementType {
  /** Multiply the running price down by `discountValue` basis points (2050 = 20.50%). */
  PERCENT_BPS_OFF = "PERCENT_BPS_OFF",
  /** Subtract `discountValue` (currency amount) from the running price. */
  AMOUNT_OFF = "AMOUNT_OFF",
  /** Set the price to `discountValue` outright. At most one per stack; applies first. */
  FIXED_PRICE = "FIXED_PRICE",
}

/**
 * What a discount element targets. `PRODUCT_CATEGORY`/`PRODUCT_LINE`/
 * `PRODUCT_FAMILY`/`PRODUCT_GROUP` match segments 0-3 of the variant's
 * PRODVIEW hierarchy (e.g. "PARTS.CIJ.EXCEL SINGLE.170"); `target_value`
 * holds the uppercased segment text. A future `PRODUCT_GROUP_ID` value is
 * reserved for referencing a real product-groups table by id.
 */
export enum DiscountElementTargetType {
  /** target_value = a specific `im_product_variants.id`. */
  PRODUCT_VARIANT_ID = "PRODUCT_VARIANT_ID",
  /** target_value = PRODVIEW segment 0, e.g. "PARTS", "FLUID", "EQUIPMENT". */
  PRODUCT_CATEGORY = "PRODUCT_CATEGORY",
  /** target_value = PRODVIEW segment 1, e.g. "CIJ", "LCM". */
  PRODUCT_LINE = "PRODUCT_LINE",
  /** target_value = PRODVIEW segment 2. */
  PRODUCT_FAMILY = "PRODUCT_FAMILY",
  /** target_value = PRODVIEW segment 3 (model group). */
  PRODUCT_GROUP = "PRODUCT_GROUP",
  /** Matches every product; target_value is null. */
  ALL_PRODUCTS = "ALL_PRODUCTS",
}

/** Lifecycle of a discount group (basket of elements created together). */
export enum DiscountGroupStatus {
  DRAFT = "DRAFT",
  PENDING_APPROVAL = "PENDING_APPROVAL",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

/** One element as fed to the stacking engine, in application order. */
export interface DiscountStackElementInput {
  id: string;
  discountType: DiscountElementType;
  /** Bps integer for PERCENT_BPS_OFF; money amount for the others. */
  discountValue: string | number;
  applicationOrder: number;
}

/** Per-element step in a computed stack, for display/audit. */
export interface DiscountStackTraceEntry {
  elementId: string;
  applicationOrder: number;
  discountType: DiscountElementType;
  discountValue: string;
  /** Running unit price before/after this element (display-rounded). */
  unitPriceBefore: string;
  unitPriceAfter: string;
  /** Per-unit reduction contributed by this element (display-rounded). */
  discountAmount: string;
}

/** Result of computing a discount stack against a list unit price. */
export interface DiscountStackResult {
  finalUnitPrice: string;
  perUnitDiscount: string;
  /** (list − final) × quantity, rounded once. */
  totalDiscountAmount: string;
  trace: DiscountStackTraceEntry[];
}

/** A product-variant candidate for an unresolved ingested row. */
export interface DiscountGroupReviewCandidate {
  productVariantId: string;
  /** 0–1 matcher confidence. */
  confidence: number;
  sku: string | null;
  label: string | null;
}

/**
 * One ingested row (discount request line / price-list row) that could not become an
 * element automatically — the review screen resolves or dismisses it.
 */
export interface DiscountGroupReviewLine {
  /** Stable key for resolve/dismiss operations. */
  key: string;
  sku: string | null;
  description: string | null;
  /** The discount the row asked for, ready to become an element once matched. */
  discountType: DiscountElementType;
  /** Bps integer for PERCENT_BPS_OFF; currency amount otherwise. */
  discountValue: number;
  maxQuantity: number | null;
  /** Effective window the resolved element should carry (from the source form). */
  effectiveStart: string | null;
  effectiveEnd: string | null;
  unresolvedReason: "sku_not_matched" | "no_price_or_percent";
  /** Near-miss matches for the review screen's picker. */
  candidates: DiscountGroupReviewCandidate[];
}

/**
 * Human fix-up state for an ingested discount group, stored under
 * `ar_discount_groups.metadata.review`. Present ⇢ the group "needs review";
 * cleared row by row as a human resolves/dismisses, then the group is
 * submitted for approval.
 */
export interface DiscountGroupReview {
  /** Identity match too weak to auto-bind; pre-selected on the review screen. */
  suggestedCustomer?: {
    customerId: string;
    customerName: string;
    /** 0–1 identity-match score. */
    score: number;
  };
  /** Extracted customer/distributor name that matched nothing. */
  unresolvedCustomerName?: string | null;
  unresolvedLines: DiscountGroupReviewLine[];
  /** Rows a human explicitly dropped (kept for audit). */
  dismissedLines?: DiscountGroupReviewLine[];
}

/** Per-line discount status computed for a v3 draft (Discounts v2 engine). */
export enum DraftLineDiscountStatusV2 {
  /** PO price matches list price — no discount needed. */
  NO_MISMATCH = "NO_MISMATCH",
  /** Below/above list with no applied stack explaining the price. */
  NEEDS_DISCOUNT = "NEEDS_DISCOUNT",
  /** An applied stack of fully-active elements explains the price. */
  COVERED = "COVERED",
  /** The applied stack contains elements whose group awaits approval. */
  PENDING_APPROVAL = "PENDING_APPROVAL",
  /** No list price could be resolved for the line. */
  NO_LIST_PRICE = "NO_LIST_PRICE",
}
